// practice-data.js - the season plan practices, as pure functions. No DOM, no
// network: Vitest covers these directly, same shape as dta-data.js.
//
// A practice is one row of coach_practices. Two kinds live in the same table:
//
//   season_week 1..N   the plan itself, one practice per week per age
//   season_week null   the reference banks (individual skill, goalkeeper,
//                      circuit, technical repetition) a coach dips into at any
//                      point in the year
//
// The bands here are the season plan's own (u7u8, u9, u10, ...), which are finer
// than the three evaluation age_groups. A U9 and a U10 team share an evaluation
// matrix band but train off different plans.

const DAY_MS = 86400000;
const ms = (d) => Date.parse(String(d).slice(0, 10) + 'T00:00:00Z');

/** Season plan bands, in the order they are shown. */
export const PRACTICE_BANDS = ['u7u8', 'u9', 'u10', 'u11u12', 'u14', 'u16', 'u19'];

/** The six blocks that carry a week, in season order. */
export const SEQUENCED_BLOCKS = [
  'pre-season', 'block-1', 'block-2', 'block-3', 'block-4', 'block-5',
];

/** The banks that sit outside the weekly sequence. */
export const REFERENCE_BLOCKS = [
  'individual', 'goalkeeper', 'circuit', 'technical-repetition',
];

/**
 * The season plan band for a team id (U9G2 -> 'u9').
 *
 * Reads the age off the id, never off age_group: every team from U7 to U12
 * carries the same "U11-U12" age_group text, so age_group cannot tell a U7 side
 * from a U12 one. Returns null for a team id with no age in it.
 */
export function practiceBandForTeamId(id) {
  const m = /^U(\d+)/i.exec(String(id || ''));
  if (!m) return null;
  const age = parseInt(m[1], 10);
  if (age <= 8)  return 'u7u8';
  if (age === 9) return 'u9';
  if (age === 10) return 'u10';
  if (age <= 12) return 'u11u12';
  if (age <= 14) return 'u14';
  if (age <= 16) return 'u16';
  return 'u19';
}

/** How a band prints. */
export function bandLabel(band) {
  return { u7u8: 'U7-U8', u9: 'U9', u10: 'U10', u11u12: 'U11-U12',
           u14: 'U14', u16: 'U16', u19: 'U19' }[band] || band || '';
}

/**
 * The date a band starts training, which is NOT the date the season starts.
 *
 * The club is back on the pitch weeks before the first federation fixture, and
 * every season plan opens with a pre-season block written for exactly those
 * weeks. `season_start` is the competition calendar (first game, what the
 * evaluation rotation counts from); this is when coaches actually start running
 * sessions.
 *
 * The ages do not start together, so the plan holds a map rather than a date.
 * In 2026-27 the older teams were on the pitch three weeks before the youngest.
 * Lookup is band, then "default", then season_start, so a plan with nothing set
 * behaves exactly as it always did.
 */
export function trainingStart(plan, band = null) {
  const map = plan?.training_starts || {};
  return (band && map[band]) || map.default || plan?.season_start || null;
}

/**
 * Which week of the season plan a date falls in, for a given band.
 *
 * Deliberately different from the evaluation rotation in eval-window.js in two
 * ways. It counts from the band's training start rather than season_start (see
 * above), and it does NOT pause for the indoor break: evaluations stop over
 * winter because there are no outdoor games to judge, but training carries
 * straight on and block 3 of every plan is the winter block written for it.
 *
 * @returns { week: number|null,
 *            reason: 'open'|'before_season'|'after_season'|'no_plan' }
 */
export function seasonWeek(plan, todayIso, band = null) {
  const start = trainingStart(plan, band);
  if (!start) return { week: null, reason: 'no_plan' };
  const today = (todayIso || '').slice(0, 10);
  if (today < start) return { week: null, reason: 'before_season' };
  if (plan.season_end && today > plan.season_end) {
    return { week: null, reason: 'after_season' };
  }
  const days = Math.floor((ms(today) - ms(start)) / DAY_MS);
  return { week: Math.floor(days / 7) + 1, reason: 'open' };
}

/**
 * This week's practice for a band.
 *
 * Every plan holds more weeks than the season has, so running off the end is not
 * expected. If it happens anyway (a long season, a short plan) the last week is
 * held rather than showing a coach nothing at all.
 */
export function practiceOfWeek(practices, band, week) {
  const mine = (practices || [])
    .filter(p => p.band === band && p.season_week != null)
    .sort((a, b) => a.season_week - b.season_week);
  if (!mine.length || !week) return null;
  return mine.find(p => p.season_week === week) || mine[mine.length - 1];
}

/** True when a practice sits outside the weekly sequence. */
export function isReference(practice) {
  return practice?.season_week == null;
}

/**
 * Title / body text in the active language, English where there is no Dutch.
 * Handles both the scalar fields and the list fields (organisation, points,
 * cues, good, questions), because the NL column is null for a list we have no
 * Dutch for at all.
 */
export function practiceText(practice, field, lang = 'en') {
  if (!practice) return '';
  const nl = practice[`${field}_nl`];
  const en = practice[field];
  if (lang === 'nl' && nl != null) {
    if (Array.isArray(nl) ? nl.length : String(nl).trim()) return nl;
  }
  return en ?? (Array.isArray(en) ? [] : '');
}

/**
 * Narrow the list. Every filter is optional and they combine with AND.
 * `skill` and `pillar` match against the tag arrays, so one practice can answer
 * several skills at once.
 */
export function filterPractices(
  practices,
  { band = null, blockKey = null, skill = null, pillar = null, reference = null } = {},
) {
  return (practices || []).filter(p => {
    if (band && p.band !== band) return false;
    if (blockKey && p.block_key !== blockKey) return false;
    if (skill && !(p.skills || []).includes(skill)) return false;
    if (pillar && !(p.pillars || []).includes(pillar)) return false;
    if (reference === true && !isReference(p)) return false;
    if (reference === false && isReference(p)) return false;
    return true;
  });
}

/**
 * Free text over both languages, so a Dutch coach finds a practice by its Dutch
 * words and an English one by its English. Blank query = passthrough.
 */
export function searchPractices(practices, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [...(practices || [])];
  return (practices || []).filter(p => {
    const hay = [
      p.title, p.title_nl, p.detail, p.detail_nl, p.block_label, p.block_label_nl,
      p.principle, p.principle_nl, p.type_label,
      ...(p.points || []), ...(p.points_nl || []),
      ...(p.cues || []), ...(p.cues_nl || []),
      ...(p.organisation || []), ...(p.organisation_nl || []),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });
}

/**
 * Group into blocks for display: the six season blocks in order, then the
 * reference banks. Empty blocks are dropped.
 */
export function groupByBlock(practices) {
  const order = [...SEQUENCED_BLOCKS, ...REFERENCE_BLOCKS];
  const bins = new Map();
  for (const p of practices || []) {
    const key = p.block_key || 'other';
    if (!bins.has(key)) bins.set(key, []);
    bins.get(key).push(p);
  }
  const out = [];
  for (const key of [...order, ...[...bins.keys()].filter(k => !order.includes(k))]) {
    const items = bins.get(key);
    if (!items?.length) continue;
    items.sort((a, b) =>
      (a.season_week ?? Infinity) - (b.season_week ?? Infinity)
      || (a.block_index - b.block_index)
      || (a.order_index - b.order_index));
    out.push({ key, label: items[0].block_label, label_nl: items[0].block_label_nl, items });
  }
  return out;
}

/**
 * Practices that train a given evaluation skill, best first.
 *
 * This is the join that a PDF cannot do: `skills` holds the canonical
 * ratings.skill slugs, so a weak criterion on a player's evaluation leads
 * straight here. Ranked by how central the skill is to the practice (a practice
 * tagged with three skills is more about each of them than one tagged with six).
 */
export function practicesForSkill(practices, skillSlug, band = null) {
  if (!skillSlug) return [];
  return filterPractices(practices, { band, skill: skillSlug })
    .sort((a, b) => (a.skills || []).length - (b.skills || []).length
      || (a.season_week ?? Infinity) - (b.season_week ?? Infinity));
}

/**
 * Turn the evaluation form's skill label ("Ball carrying") into the plan's slug
 * ("ball-carrying") using the exported vocabulary, which maps slug ->
 * { pillar, evaluation_label }. Matching is on the label because that is what a
 * rating row stores.
 */
export function skillSlugForLabel(vocabulary, label) {
  const want = String(label || '').trim().toLowerCase();
  if (!want) return null;
  for (const [slug, meta] of Object.entries(vocabulary?.skill || {})) {
    // Two shapes on purpose. The exporter emits { pillar, evaluation_label } and
    // the tests use it; the app ships a slimmed { slug: 'Label' } map, because
    // shipping the pillar too would be a kilobyte of nothing. Accepting both
    // means the client and the content pipeline cannot silently disagree, which
    // they did: a string meta made every lookup return null and the whole
    // feature just never appeared.
    const text = typeof meta === 'string' ? meta : meta?.evaluation_label;
    if (String(text || '').trim().toLowerCase() === want) return slug;
  }
  return null;
}
